// PARTNER-TOPBAR (60px, üveg NÉLKÜL — beleolvad a háttérbe, mint a belső felületen).
//
// A terv szerinti tartalom: mobil menü-gomb · lap-cím VAGY morzsa + alcím · jobbra: nyelv-váltó · harang ·
// téma · kijelentkezés. GLOBÁLIS KERESŐ SZÁNDÉKOSAN NINCS (a partnernek négy lapja van, a keresés a
// jegy-listán belül él) — ez a design-mérés egyik rögzített IA-különbsége.
import { ArrowRightStartOnRectangleIcon, Bars3Icon, FunnelIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { PortalBell } from '@/components/portal/portal-bell';
import { ThemeToggle } from '@/components/theme-toggle';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { usePortal } from '@/features/portal/portal-context';
import { logoutEverywhere } from '@/lib/auth';
import { cn } from '@/lib/utils';

/** Egy morzsa-szem: `to` nélkül az AKTUÁLIS lap (nem kattintható). */
export interface PortalCrumb {
  label: string;
  to?: '/portal/tickets';
}

export interface PortalTopbarProps {
  title?: string | undefined;
  subtitle?: string | undefined;
  crumbs?: readonly PortalCrumb[] | undefined;
  onOpenMenu: () => void;
}

export function PortalTopbar({
  title,
  subtitle,
  crumbs,
  onOpenMenu,
}: PortalTopbarProps): JSX.Element {
  // A SZŰKÍTÉS a keret-contextből jön (a topbar a shell része, tehát a provider alatt van). Csak akkor
  // jelezzük, ha van MIRŐL beszélni: konkrét fiók-scope ÉS egynél több fiók.
  const { me, accountId } = usePortal();
  const scopeName =
    accountId === null || (me?.accounts.length ?? 0) <= 1
      ? null
      : (me?.accounts.find((a) => a.id === accountId)?.name ?? null);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language === 'en' ? 'en' : 'hu';

  return (
    <header className="flex h-[60px] w-full shrink-0 items-center gap-3 bg-transparent px-4 pt-2">
      <Button
        variant="ghost"
        size="icon"
        className="size-9 md:hidden [&_svg]:size-[18px]"
        aria-label={t('portal.shell.menu')}
        onClick={onOpenMenu}
      >
        <Bars3Icon />
      </Button>

      <div className="flex min-w-0 flex-col">
        {crumbs ? (
          <nav
            aria-label={t('portal.shell.breadcrumb')}
            className="flex min-w-0 items-center gap-1.5"
          >
            {/* A morzsa-elemek a nav KÖZVETLEN gyermekei (nincs per-morzsa `span` wrapper), és CSAK az
                UTOLSÓ zsugorodik/vágódik. MIÉRT: a wrapper `min-w-0` + alap `flex-shrink: 1` volt, tehát szűk
                képernyőn a doboz a tartalma alá zsugorodott, a benne lévő `shrink-0` link viszont NEM — így a
                link kifolyt a dobozából, és a „/" BELELÓGOTT a „Jegyeim" utolsó betűjébe (user-lelet,
                2026-09-08, 375px). Wrapper nélkül a zsugorodás azon az elemen történik, ahol szabad: a
                záró morzsán (`min-w-0 truncate`), a linkek és a „/" pedig `shrink-0` maradnak. */}
            {crumbs.map((c, i) => [
              i > 0 ? (
                <span
                  key={`sep-${String(i)}`}
                  className="text-muted-foreground/45 shrink-0"
                  aria-hidden="true"
                >
                  /
                </span>
              ) : null,
              c.to !== undefined ? (
                <Link
                  key={`crumb-${String(i)}`}
                  to={c.to}
                  className="text-muted-foreground hover:text-foreground shrink-0 text-[15px] font-normal transition-colors"
                >
                  {c.label}
                </Link>
              ) : (
                <span
                  key={`crumb-${String(i)}`}
                  aria-current="page"
                  className="min-w-0 truncate text-[15px] font-semibold tracking-[-0.012em]"
                >
                  {c.label}
                </span>
              ),
            ])}
          </nav>
        ) : (
          <h1 className="truncate text-[15px] font-semibold leading-tight tracking-[-0.012em]">
            {title}
          </h1>
        )}
        {subtitle !== undefined && subtitle !== '' && (
          <Text
            variant="secondary"
            as="span"
            className="-mt-0.5 hidden truncate text-[11.5px] leading-tight sm:block"
          >
            {subtitle}
          </Text>
        )}
      </div>

      {/* SZŰKÍTÉS-JELZŐ — a rejtett szűrő elleni garancia.
          Ha a nézet EGY fiókra van szűkítve, azt a topbar kimondja: a felhasználó ne higgye adat-hibának,
          hogy kevesebb jegyet/számlát lát. „Minden fiók"-nál (és egy-fiókos partnernél) nincs mit jelezni,
          ezért a chip ilyenkor NEM jelenik meg — nulla zaj a felhasználók 79%-ának. */}
      {scopeName !== null && (
        <span
          className="border-brand/30 bg-brand-soft text-brand-dark dark:text-brand ml-3 hidden max-w-[220px] shrink-0 items-center gap-1.5 truncate rounded-full border px-2.5 py-1 text-[11.5px] font-medium md:inline-flex"
          title={t('portal.shell.scopeLabel', { name: scopeName })}
        >
          <FunnelIcon className="size-3.5 shrink-0" />
          <span className="truncate">{scopeName}</span>
        </span>
      )}

      <div className="ml-auto flex items-center gap-1">
        {/* NYELV-VÁLTÓ a topbarban (a belső felületen a profilban van): a partner nyelve a felület nyelve,
            és a névsorrend is ezen fordul — legyen egy kattintásra. */}
        <div
          role="group"
          aria-label={t('portal.shell.language')}
          className="border-border bg-muted/40 hidden gap-0.5 rounded-[9px] border p-0.5 sm:inline-flex"
        >
          {(['hu', 'en'] as const).map((l) => (
            <button
              key={l}
              type="button"
              aria-pressed={lang === l}
              onClick={() => void i18n.changeLanguage(l)}
              className={cn(
                'ease-atlas rounded-[7px] px-2 py-0.5 text-[11.5px] font-medium transition-colors duration-150',
                lang === l
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        <PortalBell />

        <ThemeToggle />
        <span className="bg-border mx-1 h-[18px] w-px" aria-hidden="true" />
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('auth.logout')}
          className="hover:bg-danger-soft hover:text-danger-600"
          onClick={() => {
            void logoutEverywhere().finally(() => void navigate({ to: '/login' }));
          }}
        >
          <ArrowRightStartOnRectangleIcon />
        </Button>
      </div>
    </header>
  );
}
